import { useQueryClient, useQuery } from "react-query";
import { freeSet } from "@coreui/icons";
import React, { useState } from "react";
import { PropTypes } from "prop-types";
import { nuclearn_api_url } from "src/App";
import axios from "axios";
import {
  CAlert,
  CButton,
  CCol,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CTextarea,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import Button from "@mui/material/Button";
import LoadingOverlay from "react-loading-overlay-ts";
import { Loading } from "src/reusable/LoadingSpinner";
import PulseLoader from "react-spinners/PulseLoader";
import "./VersionLogsButton.scss";
import { ProjectAIProductID } from "src/reusable/products";
import { ListAlt } from "@mui/icons-material";

const getVersionLogs = (req, nLines) => {
  return axios
    .get(req, {
      params: { n_lines: nLines },
    })
    .then((response) => {
      return response.data;
    });
};

const useGetVersionLogs = (req, nLines, enabled) => {
  return useQuery(["getLogs", req, nLines], () => getVersionLogs(req, nLines), {
    enabled: enabled,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

const downloadModelVersionLogs = (req, id, versionNum, callback) => {
  axios({
    url: req,
    params: {
      as_file: true,
    },
    method: "GET",
  })
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `logs-${id}-${versionNum}.txt`);
      document.body.appendChild(link);
      link.click();
    })
    .finally(() => {
      if (callback) {
        callback();
      }
    });
};

export function VersionLogs(props) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  let req = `${nuclearn_api_url}/` + props.request;
  const {
    data: logs,
    isFetching: isLoadingLogs,
    error: logError,
  } = useGetVersionLogs(req, 1000, props.isVisible);
  const queryClient = useQueryClient();
  const invalidateGetModelVersionLogs = () => {
    queryClient.invalidateQueries(["getLogs", req]);
  };
  const handleCopyLogs = () => {
    if (logs) {
      navigator.clipboard.writeText(logs).then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      });
    }
  };
  //TODO:
  // add the ability to download the logs
  return (
    <>
      <CModal
        show={props.isVisible}
        onClose={props.onModalClose}
        style={{ textAlign: "left" }}
        size="lg"
      >
        <CModalHeader>
          <CCol sm="9">
            <h3>
              {props.request.includes("model") && (
                <>
                  Model {props.id} Version {props.versionNum} logs
                </>
              )}
              {props.request.includes("job") && <>Job {props.id} logs</>}
            </h3>
          </CCol>
          <CCol className="d-flex justify-content-end" sm="3">
            <CButton
              id="mvlogs-refresh-button"
              color="info"
              style={{ marginRight: "5px" }}
              onClick={() => invalidateGetModelVersionLogs()}
              size="sm"
            >
              <CIcon content={freeSet.cilReload} />
            </CButton>
            <CButton
              id="mvlogs-copy-button"
              color={isCopied ? "success" : "info"}
              style={{ marginRight: "5px" }}
              onClick={handleCopyLogs}
              size="sm"
              disabled={logError || !logs}
              title={isCopied ? "Copied!" : "Copy logs to clipboard"}
            >
              <CIcon content={isCopied ? freeSet.cilCheckAlt : freeSet.cilCopy} />
            </CButton>
            <CButton
              id="mvlogs-close-button"
              color="secondary"
              onClick={props.onModalClose}
              size="sm"
            >
              <CIcon name="cil-x" />
            </CButton>
          </CCol>
        </CModalHeader>
        <LoadingOverlay
          active={isLoadingLogs}
          spinner={<Loading height={"75em"} width={"75em"} />}
          text="Loading Logs..."
        >
          <CModalBody>
            {logError ? (
              <CAlert color="danger">
                {logError?.response?.data?.detail
                  ? logError?.response?.data?.detail
                  : JSON.stringify(logError)}
              </CAlert>
            ) : (
              <CTextarea
                id="mvlogs-textarea"
                type="text"
                style={{
                  fontSize: "85%",
                  fontFamily: "monospace",
                  backgroundColor: "#f6f6f6",
                }}
                spellCheck="false"
                rows="25"
                name="logs"
                placeholder="Logs"
                value={logs}
                disabled={true}
              />
            )}
            <CAlert style={{ fontSize: "75%", padding: "5px" }} color="warning">
              Only the last 1000 logs are shown.
            </CAlert>
          </CModalBody>
          <CModalFooter>
            {/* If there is a error fetching logs the "download logs" button wil be disabled */}
            <CButton
              color="secondary"
              disabled={logError}
              onClick={() => {
                setIsDownloading(true);
                downloadModelVersionLogs(
                  `${nuclearn_api_url}/` + props.request,
                  props.id,
                  props.versionNum,
                  () => {
                    setIsDownloading(false);
                  },
                );
              }}
            >
              {isDownloading ? (
                <PulseLoader
                  style={{ textAlign: "center" }}
                  size={15}
                  color={"#9EA59D"}
                  loading={isDownloading}
                />
              ) : (
                "Download Logs"
              )}
            </CButton>
          </CModalFooter>
        </LoadingOverlay>
      </CModal>
    </>
  );
}

VersionLogs.propTypes = {
  isVisible: PropTypes.bool,
  onModalClose: PropTypes.func,
  request: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  versionNum: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default function VersionLogsButton(props) {
  const [isVisible, setIsVisible] = useState(false);

  const onModalClose = () => {
    setIsVisible(false);
  };
  //TODO:
  // add the ability to download the logs
  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={() => setIsVisible(true)}
        disabled={
          props.model?.model_products[0]?.products?.id === ProjectAIProductID
        }
        sx={{ ...props.sx }}
        startIcon={<ListAlt />}
      >
        View Logs
      </Button>
      {isVisible && (
        <VersionLogs
          onModalClose={onModalClose}
          isVisible={isVisible}
          request={props.request}
          id={props.id}
          versionNum={props.versionNum}
        />
      )}
    </>
  );
}

VersionLogsButton.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  versionNum: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  request: PropTypes.string,
  sx: PropTypes.any,
  styleButton: PropTypes.object,
  styleButtonText: PropTypes.object,
  model: PropTypes.object,
};
